import type { ScienceMasteryTrial } from './DiscoveryObservatoryConfig.js';

export type ScienceMasteryCheckResult = 'incomplete' | 'incorrect' | 'complete';

export interface ScienceMasteryTrialProgress {
  readonly selectedChoiceId: string | null;
  readonly hintStage: number;
  readonly attempts: number;
  readonly complete: boolean;
}

export interface DiscoveryObservatorySerializedState {
  readonly version: 1;
  readonly trialIndex: number;
  readonly completedTrialIds: readonly string[];
  readonly trialProgress: Readonly<Record<string, ScienceMasteryTrialProgress>>;
}

const blank = (): ScienceMasteryTrialProgress => ({
  selectedChoiceId: null,
  hintStage: 0,
  attempts: 0,
  complete: false,
});

function sanitize(trial: ScienceMasteryTrial, value: unknown): ScienceMasteryTrialProgress {
  const saved = value as Partial<ScienceMasteryTrialProgress> | undefined;
  return {
    selectedChoiceId:
      typeof saved?.selectedChoiceId === 'string'
      && trial.choices.some(choice => choice.id === saved.selectedChoiceId)
        ? saved.selectedChoiceId
        : null,
    hintStage: Math.max(0, Math.min(trial.hints.length, Number(saved?.hintStage) || 0)),
    attempts: Math.max(0, Number(saved?.attempts) || 0),
    complete: saved?.complete === true,
  };
}

export class DiscoveryObservatoryState {
  private trialIndex: number;
  private readonly progress: Record<string, ScienceMasteryTrialProgress> = {};
  private readonly completed = new Set<string>();

  constructor(
    private readonly trials: readonly ScienceMasteryTrial[],
    saved?: Partial<DiscoveryObservatorySerializedState>,
  ) {
    if (trials.length === 0) throw new RangeError('Discovery Observatory needs mastery trials.');
    this.trialIndex = Math.max(0, Math.min(trials.length - 1, Number(saved?.trialIndex) || 0));
    trials.forEach(trial => {
      this.progress[trial.id] = sanitize(trial, saved?.trialProgress?.[trial.id]);
    });
    (saved?.completedTrialIds ?? []).forEach(id => {
      if (trials.some(trial => trial.id === id)) this.completed.add(id);
    });
  }

  get trial(): ScienceMasteryTrial { return this.trials[this.trialIndex]; }
  get snapshot(): ScienceMasteryTrialProgress { return this.progress[this.trial.id]; }
  get mastered(): boolean { return this.completed.size === this.trials.length; }
  get hasNextTrial(): boolean { return this.trialIndex < this.trials.length - 1; }
  get completedTrialIds(): string[] { return [...this.completed]; }

  choose(id: string): boolean {
    if (this.snapshot.complete || !this.trial.choices.some(choice => choice.id === id)) return false;
    this.update({ selectedChoiceId: id });
    return true;
  }

  check(): ScienceMasteryCheckResult {
    if (!this.snapshot.selectedChoiceId) return 'incomplete';
    this.update({ attempts: this.snapshot.attempts + 1 });
    if (this.snapshot.selectedChoiceId !== this.trial.correctChoiceId) return 'incorrect';
    this.update({ complete: true });
    this.completed.add(this.trial.id);
    return 'complete';
  }

  requestHint(): string {
    const index = Math.min(this.snapshot.hintStage, this.trial.hints.length - 1);
    this.update({ hintStage: Math.min(this.trial.hints.length, index + 1) });
    return this.trial.hints[index];
  }

  nextTrial(): boolean {
    if (!this.snapshot.complete || !this.hasNextTrial) return false;
    this.trialIndex += 1;
    return true;
  }

  resetCurrent(): boolean {
    if (this.snapshot.complete) return false;
    const { hintStage, attempts } = this.snapshot;
    this.progress[this.trial.id] = { ...blank(), hintStage, attempts };
    return true;
  }

  replay(): void {
    this.trialIndex = 0;
    this.completed.clear();
    this.trials.forEach(trial => { this.progress[trial.id] = blank(); });
  }

  serialize(): DiscoveryObservatorySerializedState {
    return {
      version: 1,
      trialIndex: this.trialIndex,
      completedTrialIds: [...this.completed],
      trialProgress: Object.fromEntries(
        this.trials.map(trial => [trial.id, { ...this.progress[trial.id] }]),
      ),
    };
  }

  private update(patch: Partial<ScienceMasteryTrialProgress>): void {
    this.progress[this.trial.id] = { ...this.snapshot, ...patch };
  }
}
